import { useState } from "react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";
import { ClipboardList, MessageCircle, Phone } from "lucide-react";

import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";

import { LogActivityForm } from "../activityLog/LogActivityForm";
import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import { buildTelLink, buildWhatsAppLink } from "../dashboard/hoyPlan";
import { WhatsAppAction } from "../whatsapp/WhatsAppAction";
import { buildWhatsAppGreeting } from "../whatsapp/whatsappMessage";
import type { LeadAlertCard as LeadAlertCardData } from "./useLeadAlerts";

/**
 * Almikia Epic ER (TASK-007): the advisor's own open lead alerts on Hoy, as
 * person cards (design-system.md section 6, mirrors `HoyPersonCard.tsx`'s
 * shape) -- WhatsApp + "Registrar actividad" one tap away, capped so 100+
 * alerts never render unbounded (NFR: scalability). The caller
 * (`AdvisorToday.tsx`) already excludes the leads shown as a "Quién te
 * necesita hoy" card (`dealIdsInHoyPlan`, acceptance criterion 2) -- that
 * lead's alert still appears there instead, folded onto its own card
 * (`HoyPersonCard.tsx`'s `alert` prop), so it is never simply dropped.
 * `useAdvisorLeadAlerts()` is called ONCE by that same caller and passed
 * down here as props (NFR: one refresh call, not one per surface).
 */

const MAX_VISIBLE_ALERT_CARDS = 5;

const firstWord = (name: string): string => name.trim().split(/\s+/)[0] ?? "";

/**
 * Desktop navigates to the lead show, where `LeadAlertPills` renders the
 * same alert (acceptance criterion 4); mobile has no lead screen yet
 * (`hoyPlan.ts`'s own `cardHref` ASSUMPTION, QEA11), so it falls back to the
 * contact show.
 */
function alertCardHref(
  card: LeadAlertCardData,
  isMobile: boolean,
): string | undefined {
  if (!isMobile) return `/deals/${card.dealId}/show`;
  // Mobile has no lead screen (QEA11): a lead with no linked contact has nowhere
  // to link to, so the card renders unlinked instead of pointing at a dead route.
  return card.contactId != null
    ? `/contacts/${card.contactId}/show`
    : undefined;
}

export function LeadAlertCards({
  cards: visibleCards,
  isPending,
  error,
  refetchAlerts,
  onActivityLogged,
}: {
  /** Already excludes the leads shown as a "Quién te necesita hoy" card. */
  cards: LeadAlertCardData[];
  isPending: boolean;
  error: unknown;
  refetchAlerts: () => void;
  onActivityLogged: () => void;
}) {
  const translate = useTranslate();
  const [expanded, setExpanded] = useState(false);

  const shownCards = expanded
    ? visibleCards
    : visibleCards.slice(0, MAX_VISIBLE_ALERT_CARDS);

  return (
    <div>
      <h2 className="mb-2 text-xl font-semibold text-muted-foreground">
        {translate("crm.alerts.section_title", { _: "Alertas de tus leads" })}
      </h2>
      {error ? (
        <ListErrorState
          message={translate("crm.common.list_error")}
          onRetry={() => refetchAlerts()}
        />
      ) : isPending ? (
        <ListCardsSkeleton rows={2} />
      ) : visibleCards.length === 0 ? (
        <p className="text-base text-muted-foreground">
          {translate("crm.alerts.empty", {
            _: "No tienes alertas pendientes",
          })}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {shownCards.map((card) => (
            <LeadAlertPersonCard
              key={card.id}
              card={card}
              onActivityLogged={onActivityLogged}
              translate={translate}
            />
          ))}
          {!expanded && visibleCards.length > MAX_VISIBLE_ALERT_CARDS ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-11 self-start"
              onClick={() => setExpanded(true)}
            >
              {translate("crm.alerts.view_all", {
                _: "Ver todas",
                count: visibleCards.length,
              })}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

const LeadAlertPersonCard = ({
  card,
  onActivityLogged,
  translate,
}: {
  card: LeadAlertCardData;
  onActivityLogged: () => void;
  translate: (key: string, options?: Record<string, unknown>) => string;
}) => {
  const isMobile = useIsMobile();
  const href = alertCardHref(card, isMobile);
  const [activityLogOpen, setActivityLogOpen] = useState(false);

  const handleActivityLogOpenChange = (open: boolean) => {
    setActivityLogOpen(open);
    // Acceptance criterion 3: refresh after logging (the sheet closes both
    // on save and on cancel -- re-evaluating on either is harmless, since a
    // throttled/no-op run just re-reads the same open alerts).
    if (!open) onActivityLogged();
  };

  const NameAndReason = (
    <div className="min-w-0 flex-1">
      <div className="truncate text-base font-bold">
        {card.name}
        <StatusPill tone="not-ok" text={card.label} className="ml-2" />
      </div>
      <div className="truncate text-base text-muted-foreground">
        {card.message}
      </div>
    </div>
  );

  return (
    <div
      className={
        isMobile
          ? "flex flex-col gap-2 rounded-2xl bg-card p-3"
          : "flex items-center gap-3 rounded-2xl bg-card p-3"
      }
    >
      <div className={isMobile ? "flex items-center gap-3" : "contents"}>
        <span
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold"
        >
          {card.initials}
        </span>
        {href ? (
          <Link to={href} className="min-w-0 flex-1">
            {NameAndReason}
          </Link>
        ) : (
          NameAndReason
        )}
        {!isMobile ? (
          <AlertCardActions
            card={card}
            translate={translate}
            onLogActivity={() => setActivityLogOpen(true)}
          />
        ) : null}
      </div>
      {isMobile ? (
        <AlertCardActions
          card={card}
          translate={translate}
          onLogActivity={() => setActivityLogOpen(true)}
        />
      ) : null}
      <LogActivityForm
        dealId={card.dealId}
        open={activityLogOpen}
        onOpenChange={handleActivityLogOpenChange}
      />
    </div>
  );
};

const AlertCardActions = ({
  card,
  translate,
  onLogActivity,
}: {
  card: LeadAlertCardData;
  translate: (key: string, options?: Record<string, unknown>) => string;
  onLogActivity: () => void;
}) => (
  <div className="flex shrink-0 gap-2">
    {card.phone ? (
      <>
        {card.contact ? (
          <WhatsAppAction contact={card.contact} dealId={card.dealId} />
        ) : (
          <a
            href={buildWhatsAppLink(
              card.phone,
              buildWhatsAppGreeting({
                nombre: firstWord(card.name),
                asesora: null,
              }),
            )}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={translate("crm.whatsapp.action_label", {
              nombre: card.name,
              _: `Enviar WhatsApp a ${card.name}`,
            })}
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F5EC] text-[#23794A]"
          >
            <MessageCircle aria-hidden="true" className="h-5 w-5" />
          </a>
        )}
        <a
          href={buildTelLink(card.phone)}
          aria-label={translate("crm.dashboard.hoy.action.call")}
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FBF3D9] text-[#8A6D1D]"
        >
          <Phone aria-hidden="true" className="h-5 w-5" />
        </a>
      </>
    ) : null}
    <button
      type="button"
      onClick={onLogActivity}
      aria-label={translate("resources.deals.action.log_activity", {
        _: "Registrar actividad",
      })}
      className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"
    >
      <ClipboardList aria-hidden="true" className="h-5 w-5" />
    </button>
  </div>
);
